/**
 * Estandarización de Presets de Imágenes por Tipo de Entidad en OrderFast.
 */

export type ImageEntityType =
  | 'product'
  | 'category'
  | 'subselection'
  | 'location_banner'
  | 'location_square'
  | 'display'
  | 'display_horizontal'
  | 'display_vertical'
  | 'multilocal_brand_logo'
  | 'multilocal_brand_banner';

export interface ImagePresetConfig {
  maxWidth: number;
  maxHeight: number;
  aspectRatio: string; // ej: '1:1', '3:1', '16:9', '9:16'
  fitMode: 'cover' | 'contain';
  quality: number;
  addMargin: boolean;
  preserveAlpha: boolean;
  folder: string;
}

export const IMAGE_PRESETS: Record<ImageEntityType, ImagePresetConfig> = {
  product: {
    maxWidth: 800,
    maxHeight: 800,
    aspectRatio: '1:1',
    fitMode: 'cover',
    quality: 80,
    addMargin: true,
    preserveAlpha: false,
    folder: 'products',
  },
  category: {
    maxWidth: 600,
    maxHeight: 600,
    aspectRatio: '1:1',
    fitMode: 'contain',
    quality: 85,
    addMargin: false,
    preserveAlpha: true,
    folder: 'categories',
  },
  subselection: {
    maxWidth: 400,
    maxHeight: 400,
    aspectRatio: '1:1',
    fitMode: 'cover',
    quality: 80,
    addMargin: false,
    preserveAlpha: false,
    folder: 'subselections',
  },
  location_banner: {
    maxWidth: 1200,
    maxHeight: 400,
    aspectRatio: '3:1',
    fitMode: 'cover',
    quality: 85,
    addMargin: false,
    preserveAlpha: false,
    folder: 'locations',
  },
  location_square: {
    maxWidth: 500,
    maxHeight: 500,
    aspectRatio: '1:1',
    fitMode: 'contain',
    quality: 90,
    addMargin: false,
    preserveAlpha: true,
    folder: 'locations',
  },
  display: {
    maxWidth: 1920,
    maxHeight: 1080,
    aspectRatio: '16:9',
    fitMode: 'cover',
    quality: 85,
    addMargin: false,
    preserveAlpha: false,
    folder: 'displays',
  },
  display_horizontal: {
    maxWidth: 1920,
    maxHeight: 1080,
    aspectRatio: '16:9',
    fitMode: 'cover',
    quality: 85,
    addMargin: false,
    preserveAlpha: false,
    folder: 'displays',
  },
  display_vertical: {
    maxWidth: 1080,
    maxHeight: 1920,
    aspectRatio: '9:16',
    fitMode: 'cover',
    quality: 85,
    addMargin: false,
    preserveAlpha: false,
    folder: 'displays',
  },
  multilocal_brand_logo: {
    maxWidth: 500,
    maxHeight: 500,
    aspectRatio: '1:1',
    fitMode: 'contain',
    quality: 90,
    addMargin: false,
    preserveAlpha: true,
    folder: 'groups',
  },
  multilocal_brand_banner: {
    maxWidth: 1200,
    maxHeight: 400,
    aspectRatio: '3:1',
    fitMode: 'cover',
    quality: 85,
    addMargin: false,
    preserveAlpha: false,
    folder: 'groups',
  },
};

export function getImagePreset(entityType: ImageEntityType): ImagePresetConfig {
  return IMAGE_PRESETS[entityType] || IMAGE_PRESETS.product;
}

/**
 * Verifica si una URL ya corresponde a una imagen alojada en el Storage de Supabase.
 */
export function isSupabaseStorageUrl(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return false;
  return url.includes('.supabase.co/storage/v1/object/public/') || url.includes('/storage/v1/object/public/images/');
}

/**
 * Sanitiza y formatea URLs de imágenes (limpia espacios, normaliza enlaces de Google Drive, encodeURI).
 */
export function sanitizeAndFormatImageUrl(rawUrl?: string | null): string | null {
  if (!rawUrl || typeof rawUrl !== 'string') return null;

  let url = rawUrl.trim();
  if (!url) return null;

  // Protocol-relative URLs (//cdn.example.com/image.jpg -> https://cdn.example.com/image.jpg)
  if (url.startsWith('//')) {
    url = `https:${url}`;
  }

  // Google Drive preview/view to direct download link
  // e.g. drive.google.com/file/d/FILE_ID/view... -> drive.google.com/uc?export=download&id=FILE_ID
  const gDriveMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (gDriveMatch && gDriveMatch[1]) {
    url = `https://drive.google.com/uc?export=download&id=${gDriveMatch[1]}`;
  }

  try {
    return encodeURI(decodeURI(url));
  } catch {
    return url;
  }
}

/**
 * Imágenes por defecto (placeholders) alojadas en Supabase Storage para compatibilidad universal (Flutter, Web, APIs).
 */
const SUPABASE_STORAGE_PUBLIC_URL = process.env.NEXT_PUBLIC_SUPABASE_URL
  ? `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/object/public/images`
  : 'https://omxosvkbscwtsqrufdor.supabase.co/storage/v1/object/public/images';

export const DEFAULT_PLACEHOLDER_IMAGES: Record<ImageEntityType, string> = {
  product: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/product-placeholder.webp`,
  category: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/category-placeholder.webp`,
  subselection: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/subselection-placeholder.webp`,
  location_banner: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/product-placeholder.webp`,
  location_square: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/product-placeholder.webp`,
  display: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/product-placeholder.webp`,
  display_horizontal: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/product-placeholder.webp`,
  display_vertical: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/product-placeholder.webp`,
  multilocal_brand_logo: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/product-placeholder.webp`,
  multilocal_brand_banner: `${SUPABASE_STORAGE_PUBLIC_URL}/placeholders/product-placeholder.webp`,
};

export function getPlaceholderImageUrl(entityType: ImageEntityType = 'product'): string {
  return DEFAULT_PLACEHOLDER_IMAGES[entityType] || DEFAULT_PLACEHOLDER_IMAGES.product;
}
